/**
 * Screening for terms founders ask for that the corpus does not have.
 *
 * ## The problem this exists to prevent
 *
 * A miss on `/learn/<something>` is a demand signal — it is the most honest
 * input the content backlog can get, because it is a real founder asking a real
 * question. But the moment a typed string can influence what we write, it is an
 * injection surface: someone types "porn", and a naive pipeline queues it,
 * drafts it, and eventually publishes it on the marketing domain. Bots make
 * this worse than people, because they are tireless.
 *
 * ## Why a positive filter, not a denylist
 *
 * A denylist is the obvious answer and the wrong primary defence: abuse cannot
 * be enumerated, and every list is stale the day it ships. The domain *can* be
 * enumerated. A genuine startup term shares vocabulary with the concepts we
 * already publish — "cohort analysis" overlaps "cohort retention", "sales
 * velocity" overlaps "sales", "pipeline", "conversion". "porn" overlaps
 * nothing, and neither does any other off-domain string, without anyone having
 * had to think of it in advance.
 *
 * So the primary gate asks "does this look like it belongs to the domain we
 * write about?" rather than "is this on a list of bad words". The denylist
 * stays as a cheap second layer for strings that are on-domain-adjacent but
 * obviously unacceptable; it is the weakest layer here and is not load-bearing.
 *
 * ## What a passing score does and does not authorise
 *
 * `candidate` means "record this as demand signal". It does **not** mean
 * publish, and it does not mean draft-and-publish. Nothing an anonymous visitor
 * types can put a page on the site: the terminal state of this pipeline is a
 * ranked backlog a human reads. That ordering — score, then aggregate, then
 * human, then publish — is what keeps both the abuse vector and the
 * thin-programmatic-content SEO risk closed. Auto-published pages generated
 * from search misses are precisely what search engines demote, and they would
 * put the reviewed pages at risk by association.
 */
import { LEARN_CONCEPTS } from "@/constants/learn";

/** Longest term we will consider. Real concepts are short noun phrases. */
const MAX_TERM_LENGTH = 60;
const MIN_TERM_LENGTH = 2;
const MAX_WORDS = 6;

/**
 * Characters a legitimate concept can contain. Deliberately tight: this rejects
 * URLs, markup, and injection payloads before any scoring runs. Parentheses are
 * allowed because our own terms use them — "Ideal Customer Profile (ICP)" must
 * not be rejected by the filter guarding its own corpus.
 */
const ALLOWED_CHARS = /^[a-z0-9 \-/&.'()]+$/;

/**
 * Obviously unacceptable regardless of score. Small on purpose — see the module
 * docblock: this is the second layer, not the first. Matched on whole words so
 * "sextant" and "assessment" are not caught by substring accident.
 */
const DENIED_WORDS = new Set([
  "porn",
  "porno",
  "pornography",
  "nsfw",
  "xxx",
  "nude",
  "nudes",
  "escort",
  "escorts",
  "viagra",
  "casino",
  "gambling",
  "crack",
  "warez",
  "torrent",
]);

/**
 * Words that carry no domain signal, so their presence should not make an
 * off-domain phrase look on-domain.
 */
const STOP_WORDS = new Set([
  "a", "an", "and", "the", "of", "for", "to", "in", "on", "is", "are", "what",
  "how", "why", "do", "does", "my", "our", "your", "with", "vs", "versus",
  "or", "by", "at", "from", "it", "its", "that", "this",
]);

/**
 * Domain vocabulary, derived from the corpus itself plus a small set of anchor
 * words for areas we intend to cover but have not written yet.
 *
 * Deriving most of it from the corpus is the point: the filter widens
 * automatically as the library grows, with no list to maintain. A concept added
 * next month brings its own vocabulary with it.
 *
 * **Naming fields plus the one-sentence definition, never the long-form
 * prose.** `term`, `aliases`, and `domain` are compressed domain language.
 * `shortDefinition` is a sentence, so it does carry incidental words in with
 * it (`color`, `family`, `paper`), and strings like "color palette" reach the
 * review queue because of them. That is a deliberate trade, because the two
 * verdicts are not symmetric: a false accept costs one row in a
 * human-reviewed backlog and someone sees it, while a false reject is
 * terminal and silent. The filter is tuned to prefer the visible mistake.
 * `whyItMatters`, `workedExample`, and `commonMistakes` are the ones held
 * out, because they are long enough that every content batch would widen the
 * boundary in a direction no author is thinking about while writing. Restore
 * `whyItMatters` here and "cancel my flight" and "how to cancel netflix" both
 * score 0.50 and pass; they are in the rejection cases of
 * `learn.termProposal.test.ts` for that reason.
 *
 * The corollary is the ANCHORS list below, which is not optional garnish: with
 * prose excluded, a legitimate word the corpus has not published yet has
 * nowhere else to come from. Narrowing the harvest without extending the
 * anchors silently dropped `ebitda`, `headcount`, `forecast`, and `leverage`
 * to `off-domain`, and an off-domain verdict is terminal and silent on every
 * surface: no `learn_term_missed`, no `LearnRequest` row, no trace that the
 * founder ever asked. Adding a word here is how the filter learns a domain
 * the corpus has not reached.
 */
function buildDomainVocabulary(): ReadonlySet<string> {
  const vocabulary = new Set<string>();

  const add = (text: string) => {
    for (const word of text.toLowerCase().split(/[^a-z0-9]+/)) {
      if (word.length > 2 && !STOP_WORDS.has(word)) vocabulary.add(word);
    }
  };

  for (const concept of LEARN_CONCEPTS) {
    add(concept.term);
    for (const alias of concept.aliases) add(alias);
    add(concept.domain);
    add(concept.shortDefinition);
  }

  // Anchors for parts of the domain the corpus has not reached yet, so a
  // legitimate ask about them is not rejected for the circular reason that we
  // have not written about them.
  for (const anchor of [
    "startup", "founder", "venture", "investor", "fundraising", "seed",
    "series", "valuation", "cap", "table", "equity", "dilution", "safe",
    "convertible", "note", "term", "sheet", "runway", "burn", "revenue",
    "margin", "pricing", "sales", "marketing", "growth", "retention",
    "churn", "customer", "market", "product", "pivot", "traction", "metrics",
    "cohort", "funnel", "conversion", "acquisition", "onboarding", "hiring",
    "cofounder", "vesting", "board", "advisor", "accelerator", "incubator",
    "pitch", "deck", "diligence", "acquisition", "exit", "ipo", "moat",
    "positioning", "segment", "persona", "pipeline", "quota", "saas",
    "subscription", "freemium", "enterprise", "procurement", "compliance",
    // Finance and operations vocabulary the corpus has not published yet.
    // Added when the prose harvest was removed above, because these were
    // reaching the review queue only via incidental words in `whyItMatters`.
    // "invoice" is deliberately absent: it carries no startup-specific sense
    // and it is the word that turns the off-domain "invoice template word"
    // into a candidate, which is a rejection case below.
    "ebitda", "headcount", "payroll", "salary", "forecast",
    "leverage", "underwriting", "bottleneck", "incentive", "budget",
    "capital", "cash", "profit", "expense", "bookkeeping", "audit",
    // Dropping `whyItMatters` also cost `planning`, which took real asks with
    // it: "territory planning" and "layoff planning" both went from candidate
    // to a silent off-domain rejection. The fix is the specific word, not
    // `planning` itself, which would let "wedding planning" clear the
    // threshold on two words.
    "layoff", "territory",
  ]) {
    vocabulary.add(anchor);
  }

  return vocabulary;
}

const DOMAIN_VOCABULARY = buildDomainVocabulary();

/** Exact terms and aliases already published, for the "we have this" case. */
const KNOWN_TERMS = new Map<string, string>(
  LEARN_CONCEPTS.flatMap((concept) =>
    [concept.term, ...concept.aliases].map(
      (phrase) => [phrase.toLowerCase(), concept.slug] as const,
    ),
  ),
);

/**
 * Where a missed term came from. Lives here rather than beside either analytics
 * helper because both a client module and a server module need it, and a
 * hand-copied union drifts the moment a third surface is added (CLAUDE.md).
 * A runtime const with the type derived from it, not a bare union, because the
 * term-request route must VALIDATE surfaces at runtime — a hand-copied array
 * there would compile unchanged when this list grows, silently relabeling every
 * new surface as `learn-index` in the notification email (PR #596 review).
 *
 * The surfaces are not equal signal, which is the whole reason to distinguish
 * them. A search miss is someone actively looking. A 404 can as easily be a
 * stale link or a typo. A chat lookup is the strongest of the three: the
 * founder asked a question in their own words and Lev had nothing authored to
 * answer it with.
 *
 * `learn-concept` is a reader who got all the way through one definition and
 * still had to ask, which makes it closer in strength to a chat lookup than to
 * an ambient index miss — they were engaged, on-domain, and specific. The
 * digest ranks by surface for exactly this reason, so a new surface is additive
 * there rather than something to fold into an existing bucket.
 *
 * `learn-topic` is a hub browser — between the index's ambient miss and a
 * concept reader's specific one. `labs-learn` is the strongest signal of all:
 * a signed-in founder asking from inside the product, where nothing anonymous
 * ever reaches.
 *
 * `learn-guides` and `learn-guide` are the two Guide surfaces, and they are
 * why the surface list is load-bearing rather than descriptive. A visitor is
 * never asked whether they want a term or a process walkthrough — the request
 * box deliberately refuses to make them classify their own ask (see
 * `LearnSuggestTerm`). An ask made from a guide page is the closest thing to
 * that intent we can collect without asking, so it seeds the admin console's
 * term-or-guide classification instead of the visitor doing it.
 */
export const LEARN_TERM_MISS_SURFACES = [
  "learn-index",
  "learn-concept",
  "learn-topic",
  "learn-not-found",
  "learn-guides",
  "learn-guide",
  "labs-learn",
  "levai-chat",
] as const;

export type LearnTermMissSurface = (typeof LEARN_TERM_MISS_SURFACES)[number];

/**
 * Surfaces whose asks most likely want a Guide rather than a concept page.
 * A default for the admin console's `kind`, never a decision: the console can
 * retag any row, and this only saves the common case a click.
 */
export const GUIDE_LEANING_SURFACES: readonly LearnTermMissSurface[] = [
  "learn-guides",
  "learn-guide",
];

export type TermProposalVerdict =
  /** Already in the corpus — send them to the page rather than queueing it. */
  | { status: "known"; slug: string; normalized: string }
  /** Plausibly on-domain. Record as demand signal for human review. */
  | { status: "candidate"; normalized: string; score: number }
  /** Do not record. `reason` is for our logs, never shown as an accusation. */
  | {
      status: "rejected";
      reason: "empty" | "too-short" | "too-long" | "too-many-words" | "bad-characters" | "denied" | "off-domain";
    };

/**
 * Lowercases, collapses whitespace, and strips decorative wrapping punctuation.
 *
 * The strip set is enumerated rather than "any non-alphanumeric" on purpose: a
 * greedy strip eats the closing bracket of "ideal customer profile (icp)",
 * which then fails both the character check and the exact-match lookup against
 * the very corpus entry it names.
 */
export function normalizeTerm(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/^["'*?!,.:;\s]+|["'*?!,.:;\s]+$/g, "")
    .trim();
}

/**
 * Fraction of a phrase's meaningful words that belong to the domain
 * vocabulary, 0–1. Exported for the tests, which assert the ordering of real
 * examples rather than hard-coded numbers.
 */
export function domainRelevanceScore(normalized: string): number {
  const words = normalized
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2 && !STOP_WORDS.has(word));
  if (words.length === 0) return 0;

  const hits = words.filter((word) => {
    if (DOMAIN_VOCABULARY.has(word)) return true;
    // Cheap singular/plural tolerance — "cohorts" should score like "cohort".
    if (word.endsWith("s") && DOMAIN_VOCABULARY.has(word.slice(0, -1))) return true;
    return false;
  }).length;

  return hits / words.length;
}

/**
 * Minimum share of on-domain words for a phrase to be recorded.
 *
 * Set so that a phrase needs a real domain word in it, while tolerating one
 * unfamiliar word alongside a familiar one ("sales velocity" → 0.5). Tuned
 * against the cases in `learn.termProposal.test.ts`; change it there first.
 */
export const MIN_DOMAIN_RELEVANCE = 0.5;

/**
 * Screens a term a visitor asked for. Pure and synchronous — no model call, no
 * network, no database — so it can run on any surface, including a static
 * 404 page's client bundle.
 */
export function screenTermProposal(raw: string): TermProposalVerdict {
  const normalized = normalizeTerm(raw);

  if (!normalized) return { status: "rejected", reason: "empty" };
  if (normalized.length < MIN_TERM_LENGTH)
    return { status: "rejected", reason: "too-short" };
  if (normalized.length > MAX_TERM_LENGTH)
    return { status: "rejected", reason: "too-long" };
  if (!ALLOWED_CHARS.test(normalized))
    return { status: "rejected", reason: "bad-characters" };

  const words = normalized.split(" ");
  if (words.length > MAX_WORDS)
    return { status: "rejected", reason: "too-many-words" };

  const known = KNOWN_TERMS.get(normalized);
  if (known) return { status: "known", slug: known, normalized };

  // Tokenise on non-alphanumerics, not on spaces. Stripping punctuation from
  // each space-delimited word instead lets a denied token ride into the corpus
  // attached to an allowed one: "porn-market" collapses to "pornmarket", misses
  // the denylist, then scores 0.5 because "market" is on-domain. Tokenising
  // matches how `domainRelevanceScore` splits, so the two layers see the same
  // words. The whole-word form is checked too, which catches "p.o.r.n".
  const deniedCandidates = [
    ...normalized.split(/[^a-z0-9]+/),
    ...words.map((word) => word.replace(/[^a-z0-9]/g, "")),
  ];
  for (const candidate of deniedCandidates) {
    if (DENIED_WORDS.has(candidate))
      return { status: "rejected", reason: "denied" };
  }

  const score = domainRelevanceScore(normalized);
  if (score < MIN_DOMAIN_RELEVANCE)
    return { status: "rejected", reason: "off-domain" };

  return { status: "candidate", normalized, score };
}
